import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useApiKeysStore } from '@/stores/apiKeys';
import { useNotification } from '@asabdev/asab-ui';
import type {
  ApiKey,
  ApiKeyFilters,
  ApiKeyPagination,
} from '@/features/api-keys/types';
import {
  getApiKeyStatus,
  getApiKeyStatusColor,
  formatApiKeyDate,
  formatTimeAgo,
  formatExpiryInfo,
  copyToClipboard,
  sortApiKeysByDate,
  sortApiKeysByName,
  filterApiKeysByStatus,
  searchApiKeys,
  getApiKeyStats,
} from '@/features/api-keys/utils';

export function useApiKeys() {
  const store = useApiKeysStore();
  const { showSuccess, showError } = useNotification();
  const {
    apiKeys,
    selectedApiKey,
    isLoading,
    error,
    total,
    currentPage,
    perPage,
    totalPages,
    hasApiKeys,
    createKeyModalOpen,
    deleteKeyModalOpen,
  } = storeToRefs(store);

  // Local search and filter state
  const searchTerm = ref('');
  const selectedStatus = ref('all');
  const sortBy = ref<'date' | 'name'>('date');

  // Computed filtered and sorted API keys
  const filteredApiKeys = computed(() => {
    let keys = [...apiKeys.value];

    if (searchTerm.value.trim()) {
      keys = searchApiKeys(keys, searchTerm.value);
    }

    if (selectedStatus.value !== 'all') {
      keys = filterApiKeysByStatus(keys, selectedStatus.value);
    }

    if (sortBy.value === 'name') {
      keys = sortApiKeysByName(keys);
    } else {
      keys = sortApiKeysByDate(keys);
    }

    return keys;
  });

  // API key statistics
  const apiKeyStats = computed(() => getApiKeyStats(apiKeys.value));

  // Copy API key to clipboard
  const copyApiKey = async (apiKey: string) => {
    const success = await copyToClipboard(apiKey);
    if (success) {
      showSuccess('API key copied to clipboard!');
    } else {
      showError('Failed to copy API key to clipboard');
    }
    return success;
  };

  // Utility functions for templates
  const getStatusInfo = (apiKey: ApiKey) => {
    const status = getApiKeyStatus(apiKey);
    const label = status === 'expiring_soon' ? 'Expiring Soon' : status.charAt(0).toUpperCase() + status.slice(1);
    return {
      status,
      color: getApiKeyStatusColor(status),
      text: label,
    };
  };

  const formatDate = formatApiKeyDate;
  const formatTime = formatTimeAgo;
  const formatExpiry = formatExpiryInfo;

  // Refresh data
  const refresh = async (filters?: ApiKeyFilters, pagination?: Partial<ApiKeyPagination>) => {
    await store.fetchApiKeys(filters, pagination);
  };

  // Load initial data if not already loaded
  const initialize = async () => {
    if (apiKeys.value.length === 0 && !isLoading.value) {
      await store.fetchApiKeys();
    }
  };

  return {
    // Store state
    apiKeys,
    selectedApiKey,
    isLoading,
    error,
    total,
    currentPage,
    perPage,
    totalPages,
    hasApiKeys,

    // Computed
    filteredApiKeys,
    apiKeyStats,

    // Local state
    searchTerm,
    selectedStatus,
    sortBy,

    // CRUD operations
    createApiKey: store.createApiKey,
    updateApiKey: store.updateApiKey,
    deleteApiKey: store.deleteApiKey,
    getApiKey: store.getApiKey,
    getApiKeyUsage: store.getApiKeyUsage,
    rotateApiKey: store.rotateApiKey,

    // Utility actions
    copyApiKey,
    refresh,
    initialize,

    // Modal management
    openCreateKeyModal: store.openCreateKeyModal,
    closeCreateKeyModal: store.closeCreateKeyModal,
    openDeleteKeyModal: store.openDeleteKeyModal,
    closeDeleteKeyModal: store.closeDeleteKeyModal,
    createKeyModalOpen,
    deleteKeyModalOpen,

    // Pagination
    setPage: store.setPage,
    setPerPage: store.setPerPage,

    // Utility functions
    getStatusInfo,
    formatDate,
    formatTime,
    formatExpiry,

    // Store utilities
    findApiKeyById: store.findApiKeyById,
    copyApiKeyToClipboard: store.copyApiKeyToClipboard,
    resetStore: store.resetStore,
  };
}
